import { useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Dropdown } from "antd";
import { colorForShift } from "../../components/common/HierarchyShift";
import { formatTimeRangeToAMPM } from "../../components/common/CommonFunctions";
import { cn } from "../../utils/tailwindUtils";

const INLINE_MAX = 5;

const ShiftTag = ({ shift, shifts, muted = false }) => {
    const tone = colorForShift(shift.id, shifts);
    return (
        <span
            title={`${shift.name}${shift.shift_timings_from ? ` (${formatTimeRangeToAMPM(shift.shift_timings_from, shift.shift_timings_to)})` : ""}`}
            className={cn(
                "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-semibold",
                muted && "opacity-45",
            )}
            style={{ background: tone.bg, color: tone.text, border: `1px solid ${tone.ring}` }}
        >
            {shift.code} · {shift.name}{shift.is_night_shift ? " 🌙" : ""}
            {shift.is_unused ? " · unused" : ""}
        </span>
    );
};

/**
 * Compact shift legend — shows active shifts inline, collapses the rest.
 */
const ShiftLegend = ({ shifts = [], roster = null }) => {
    const [menuOpen, setMenuOpen] = useState(false);

    const { inline, overflow } = useMemo(() => {
        const active = shifts.filter((s) => !s.is_unused);
        const unused = shifts.filter((s) => s.is_unused);
        const prioritized = [...active, ...unused];
        return {
            inline: prioritized.slice(0, INLINE_MAX),
            overflow: prioritized.slice(INLINE_MAX),
        };
    }, [shifts]);

    const overflowMenu = {
        items: overflow.map((shift) => ({
            key: String(shift.id),
            label: (
                <span className="inline-flex items-center gap-2 py-0.5">
                    <ShiftTag shift={shift} shifts={shifts} muted={shift.is_unused} />
                </span>
            ),
        })),
    };

    return (
        <div className="mx-3 flex flex-wrap items-center gap-2 px-3 pb-2">
            {inline.map((shift) => (
                <ShiftTag key={shift.id} shift={shift} shifts={shifts} muted={shift.is_unused} />
            ))}
            {overflow.length > 0 && (
                <Dropdown
                    menu={overflowMenu}
                    trigger={["click"]}
                    open={menuOpen}
                    onOpenChange={setMenuOpen}
                >
                    <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
                    >
                        +{overflow.length} more
                        <ChevronDown className={cn("h-3 w-3 transition-transform", menuOpen && "rotate-180")} />
                    </button>
                </Dropdown>
            )}
            <span className="text-[11px] text-muted-foreground">WO week off · L leave · H holiday</span>
            {roster && (
                <span className={cn(
                    "ml-auto inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium",
                    roster.is_published
                        ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                        : "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
                )}>
                    {roster.is_published ? "Published" : "Draft — not affecting attendance"}
                </span>
            )}
        </div>
    );
};

export default ShiftLegend;
